import { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import ConfirmPasswordModal from '../../../components/feature/ConfirmPasswordModal';
import BulkImportStaffModal from '../../../components/feature/BulkImportStaffModal';
import { logAdminPromotion, logAdminCreation, logAdminDeletion, logAdminRevocation } from '../../../lib/accessLogs';

export default function AdministrateursPage() {
  const { user, role, userRoles, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const isTrueSuperAdmin = userRoles?.includes('super_admin') ?? false;
  const [loading, setLoading] = useState(false);
  const [administrateurs, setAdministrateurs] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState<'create' | 'promote' | 'list'>('list');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Formulaire création
  const [createForm, setCreateForm] = useState({
    nom: '',
    prenom: '',
    email: '',
    motDePasse: '',
    sexe: '',
  });
  const [loadingCreate, setLoadingCreate] = useState(false);

  // Formulaire promotion
  const [promoteEmail, setPromoteEmail] = useState('');
  const [loadingPromote, setLoadingPromote] = useState(false);

  // Modales de confirmation
  const [showConfirmPromote, setShowConfirmPromote] = useState(false);
  const [showPasswordPromote, setShowPasswordPromote] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [showPasswordDelete, setShowPasswordDelete] = useState(false);
  const [showConfirmRevoke, setShowConfirmRevoke] = useState(false);
  const [showPasswordRevoke, setShowPasswordRevoke] = useState(false);
  const [pendingAction, setPendingAction] = useState<{
    type: 'delete' | 'revoke';
    adminId: string;
    userId: string;
    adminName: string;
  } | null>(null);

  const [showImportModal, setShowImportModal] = useState(false);

  // Changement de mot de passe
  const [showPasswordAdmin, setShowPasswordAdmin] = useState<string | null>(null);
  const [newPasswordAdmin, setNewPasswordAdmin] = useState('');
  const [confirmPasswordAdmin, setConfirmPasswordAdmin] = useState('');
  const [passwordAdminError, setPasswordAdminError] = useState('');
  const [passwordAdminSuccess, setPasswordAdminSuccess] = useState('');
  const [loadingPasswordAdmin, setLoadingPasswordAdmin] = useState(false);

  useEffect(() => {
    if (authLoading) return;

    if (!user) {
      navigate('/login', { replace: true });
      return;
    }

    if (role !== 'super_admin') {
      navigate('/', { replace: true });
      return;
    }

    loadAdministrateurs();
  }, [user, role, authLoading, navigate]);

  const loadAdministrateurs = async () => {
    setLoading(true);
    const { data, error: loadError } = await supabase
      .from('super_admins')
      .select('*')
      .order('created_at', { ascending: false });

    if (loadError) {
      console.error('Erreur chargement administrateurs:', loadError);
      setLoading(false);
      return;
    }

    if (data) {
      const enriched = await Promise.all(data.map(async (admin: any) => {
        const { data: medecin } = await supabase
          .from('medecins')
          .select('specialite, email')
          .eq('user_id', admin.user_id)
          .maybeSingle();

        const { data: secretaire } = await supabase
          .from('secretaires')
          .select('id')
          .eq('user_id', admin.user_id)
          .maybeSingle();

        const otherRoles: string[] = [];
        if (medecin) otherRoles.push('Médecin' + (medecin.specialite ? ` (${medecin.specialite})` : ''));
        if (secretaire) otherRoles.push('Secrétaire');

        return {
          ...admin,
          isAlsoMedecin: !!medecin,
          isAlsoSecretaire: !!secretaire,
          otherRoles,
        };
      }));

      setAdministrateurs(enriched);
    }
    setLoading(false);
  };

  const handleCreateAdmin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoadingCreate(true);
    setError('');
    setSuccess('');

    try {
      const { data: authData } = await supabase.auth.getSession();
      if (!authData.session) {
        throw new Error('Session expirée');
      }

      const response = await supabase.functions.invoke('create-super-admin', {
        body: {
          nom: createForm.nom,
          prenom: createForm.prenom,
          email: createForm.email,
          motDePasse: createForm.motDePasse,
          sexe: createForm.sexe || null,
        },
        headers: {
          Authorization: `Bearer ${authData.session.access_token}`,
        },
      });

      if (response.error) throw response.error;

      const fullName = `${createForm.prenom} ${createForm.nom}`;
      const newUserId = response.data?.data?.user_id;

      if (newUserId) {
        await logAdminCreation(user.id, newUserId, fullName, createForm.email);
      }

      setSuccess('Administrateur créé avec succès !');
      setCreateForm({ nom: '', prenom: '', email: '', motDePasse: '', sexe: '' });
      setActiveTab('list');
      loadAdministrateurs();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la création du compte');
      setTimeout(() => setError(''), 5000);
    } finally {
      setLoadingCreate(false);
    }
  };

  // Étape 1 : Afficher le popup de confirmation
  const handlePromoteClick = (e: React.FormEvent) => {
    e.preventDefault();
    if (!promoteEmail) return;
    setShowConfirmPromote(true);
  };

  // Étape 2 : Après confirmation, afficher le popup de mot de passe
  const handleConfirmPromote = () => {
    setShowConfirmPromote(false);
    setShowPasswordPromote(true);
  };

  // Étape 3 : Après vérification du mot de passe, exécuter la promotion
  const handlePasswordVerifiedPromote = async () => {
    setShowPasswordPromote(false);
    setLoadingPromote(true);
    setError('');
    setSuccess('');

    try {
      const { data: authData } = await supabase.auth.getSession();
      if (!authData.session) {
        throw new Error('Session expirée');
      }

      const response = await supabase.functions.invoke('promote-to-super-admin', {
        body: {
          email: promoteEmail,
          adminPasswordVerified: true,
        },
        headers: {
          Authorization: `Bearer ${authData.session.access_token}`,
        },
      });

      if (response.error) throw response.error;

      // Logger la promotion
      const targetUserId = response.data?.data?.user_id;
      const targetName = response.data?.data?.nom && response.data?.data?.prenom
        ? `${response.data.data.prenom} ${response.data.data.nom}`
        : promoteEmail;

      if (targetUserId) {
        await logAdminPromotion(user.id, targetUserId, targetName, promoteEmail);
      }

      setSuccess(response.data?.message || 'Utilisateur promu administrateur avec succès !');
      setPromoteEmail('');
      setActiveTab('list');
      loadAdministrateurs();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la promotion');
      setTimeout(() => setError(''), 5000);
    } finally {
      setLoadingPromote(false);
    }
  };

  // Suppression avec confirmation + mot de passe
  const handleDeleteClick = (adminId: string, userId: string, adminName: string) => {
    setPendingAction({ type: 'delete', adminId, userId, adminName });
    setShowConfirmDelete(true);
  };

  const handleConfirmDelete = () => {
    setShowConfirmDelete(false);
    setShowPasswordDelete(true);
  };

  const handlePasswordVerifiedDelete = async () => {
    setShowPasswordDelete(false);
    if (!pendingAction) return;

    const { adminId, userId: targetUserId, adminName } = pendingAction;

    try {
      const { error: adminError } = await supabase
        .from('super_admins')
        .delete()
        .eq('id', adminId);

      if (adminError) throw adminError;

      const { error: userError } = await supabase.auth.admin.deleteUser(targetUserId);
      if (userError) console.error('Erreur suppression utilisateur:', userError);

      await logAdminDeletion(user.id, targetUserId, adminName);

      setSuccess('Administrateur supprimé avec succès');
      await loadAdministrateurs();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError('Erreur lors de la suppression de l\'administrateur');
      setTimeout(() => setError(''), 3000);
    } finally {
      setPendingAction(null);
    }
  };

  // Révocation avec confirmation + mot de passe
  const handleRevokeClick = (adminId: string, userId: string, adminName: string) => {
    setPendingAction({ type: 'revoke', adminId, userId, adminName });
    setShowConfirmRevoke(true);
  };

  const handleConfirmRevoke = () => {
    setShowConfirmRevoke(false);
    setShowPasswordRevoke(true);
  };

  const handlePasswordVerifiedRevoke = async () => {
    setShowPasswordRevoke(false);
    if (!pendingAction) return;

    const { adminId, adminName } = pendingAction;

    try {
      const { error: adminError } = await supabase
        .from('super_admins')
        .delete()
        .eq('id', adminId);

      if (adminError) throw adminError;

      await logAdminRevocation(user.id, pendingAction.userId, adminName);

      setSuccess(`Rôle d'administrateur retiré à ${adminName}.`);
      await loadAdministrateurs();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError('Erreur lors du retrait du rôle');
      setTimeout(() => setError(''), 3000);
    } finally {
      setPendingAction(null);
    }
  };

  const handlePasswordChangeAdmin = async (userId: string, adminName: string) => {
    setPasswordAdminError('');
    setPasswordAdminSuccess('');

    if (!newPasswordAdmin || newPasswordAdmin.length < 10) {
      setPasswordAdminError('Le mot de passe doit contenir au moins 10 caractères');
      return;
    }

    if (newPasswordAdmin !== confirmPasswordAdmin) {
      setPasswordAdminError('Les mots de passe ne correspondent pas');
      return;
    }

    setLoadingPasswordAdmin(true);

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;

      if (!token) {
        setPasswordAdminError('Session expirée, veuillez vous reconnecter');
        setLoadingPasswordAdmin(false);
        return;
      }

      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
      const fnUrl = `${supabaseUrl}/functions/v1/admin-reset-password`;

      const response = await fetch(fnUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ userId, newPassword: newPasswordAdmin }),
      });

      const result = await response.json();

      if (!response.ok || !result?.success) {
        setPasswordAdminError(result?.error || `Erreur serveur (${response.status})`);
      } else {
        setPasswordAdminSuccess(`Mot de passe de ${adminName} changé avec succès !`);
        setNewPasswordAdmin('');
        setConfirmPasswordAdmin('');
        setTimeout(() => {
          setPasswordAdminSuccess('');
          setShowPasswordAdmin(null);
        }, 2500);
      }
    } catch (err: any) {
      setPasswordAdminError(err.message || 'Erreur inattendue');
    } finally {
      setLoadingPasswordAdmin(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-4-line text-4xl text-clinic-blue animate-spin mb-4"></i>
          <div className="text-clinic-blue-dark text-xl">Chargement...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* Modale confirmation promotion */}
      <ConfirmPasswordModal
        isOpen={showPasswordPromote}
        title="Vérification de sécurité"
        message={`Vous allez promouvoir ${promoteEmail} en tant qu'administrateur. Cette action sera enregistrée dans les journaux d'audit. Veuillez entrer votre mot de passe pour confirmer.`}
        confirmLabel="Confirmer la promotion"
        onConfirm={handlePasswordVerifiedPromote}
        onCancel={() => setShowPasswordPromote(false)}
      />

      {/* Modale confirmation suppression */}
      <ConfirmPasswordModal
        isOpen={showPasswordDelete}
        title="Confirmation de suppression"
        message={`Vous allez supprimer définitivement l'administrateur ${pendingAction?.adminName || ''}. Cette action est irréversible et sera enregistrée dans les journaux d'audit. Veuillez entrer votre mot de passe pour confirmer.`}
        confirmLabel="Supprimer définitivement"
        onConfirm={handlePasswordVerifiedDelete}
        onCancel={() => {
          setShowPasswordDelete(false);
          setPendingAction(null);
        }}
      />

      {/* Modale confirmation révocation */}
      <ConfirmPasswordModal
        isOpen={showPasswordRevoke}
        title="Confirmation de rétrogradation"
        message={`Vous allez retirer le rôle d'administrateur à ${pendingAction?.adminName || ''}. Son compte restera actif mais il n'aura plus accès à l'administration. Cette action sera enregistrée dans les journaux d'audit.`}
        confirmLabel="Rétrograder"
        onConfirm={handlePasswordVerifiedRevoke}
        onCancel={() => {
          setShowPasswordRevoke(false);
          setPendingAction(null);
        }}
      />

      {/* Popups de confirmation simples (étape 1) */}
      {showConfirmPromote && (
        <div className="fixed inset-0 z-[9998] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowConfirmPromote(false)} />
          <div className="relative bg-white rounded-3xl shadow-2xl p-8 max-w-md w-full mx-4 z-10">
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-arrow-up-circle-line text-3xl text-amber-600"></i>
            </div>
            <h2 className="text-xl font-bold text-center text-gray-900 mb-2">Promouvoir en administrateur ?</h2>
            <p className="text-sm text-gray-600 text-center mb-2">
              Vous allez donner les droits d'administration complets à <strong>{promoteEmail}</strong>.
            </p>
            <p className="text-xs text-amber-600 text-center mb-6 bg-amber-50 rounded-xl p-3">
              <i className="ri-information-line mr-1"></i>
              Cette personne pourra gérer les médecins, secrétaires, patients et accéder à toutes les données de la clinique.
            </p>
            <div className="flex gap-3">
              <button onClick={() => setShowConfirmPromote(false)} className="flex-1 px-4 py-3 border-2 border-gray-200 text-gray-700 rounded-full hover:bg-gray-50 font-medium transition-all whitespace-nowrap">
                Annuler
              </button>
              <button onClick={handleConfirmPromote} className="flex-1 px-4 py-3 bg-green-600 text-white rounded-full hover:bg-green-700 font-semibold transition-all shadow-lg whitespace-nowrap">
                <i className="ri-check-line mr-2"></i>
                Oui, promouvoir
              </button>
            </div>
          </div>
        </div>
      )}

      {showConfirmDelete && (
        <div className="fixed inset-0 z-[9998] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => { setShowConfirmDelete(false); setPendingAction(null); }} />
          <div className="relative bg-white rounded-3xl shadow-2xl p-8 max-w-md w-full mx-4 z-10">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-delete-bin-line text-3xl text-red-600"></i>
            </div>
            <h2 className="text-xl font-bold text-center text-gray-900 mb-2">Supprimer cet administrateur ?</h2>
            <p className="text-sm text-gray-600 text-center mb-2">
              Vous allez supprimer définitivement le compte de <strong>{pendingAction?.adminName || ''}</strong>.
            </p>
            <p className="text-xs text-red-600 text-center mb-6 bg-red-50 rounded-xl p-3">
              <i className="ri-error-warning-line mr-1"></i>
              Cette action est <strong>irréversible</strong>. Le compte utilisateur sera entièrement supprimé.
            </p>
            <div className="flex gap-3">
              <button onClick={() => { setShowConfirmDelete(false); setPendingAction(null); }} className="flex-1 px-4 py-3 border-2 border-gray-200 text-gray-700 rounded-full hover:bg-gray-50 font-medium transition-all whitespace-nowrap">
                Annuler
              </button>
              <button onClick={handleConfirmDelete} className="flex-1 px-4 py-3 bg-red-600 text-white rounded-full hover:bg-red-700 font-semibold transition-all shadow-lg whitespace-nowrap">
                <i className="ri-check-line mr-2"></i>
                Oui, supprimer
              </button>
            </div>
          </div>
        </div>
      )}

      {showConfirmRevoke && (
        <div className="fixed inset-0 z-[9998] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => { setShowConfirmRevoke(false); setPendingAction(null); }} />
          <div className="relative bg-white rounded-3xl shadow-2xl p-8 max-w-md w-full mx-4 z-10">
            <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-arrow-down-circle-line text-3xl text-yellow-600"></i>
            </div>
            <h2 className="text-xl font-bold text-center text-gray-900 mb-2">Rétrograder cet administrateur ?</h2>
            <p className="text-sm text-gray-600 text-center mb-2">
              Vous allez retirer le rôle d'administrateur à <strong>{pendingAction?.adminName || ''}</strong>.
            </p>
            <p className="text-xs text-gray-500 text-center mb-6 bg-gray-50 rounded-xl p-3">
              <i className="ri-information-line mr-1"></i>
              Son compte restera actif avec son rôle actuel (médecin/secrétaire), mais il n'aura plus accès au tableau de bord d'administration.
            </p>
            <div className="flex gap-3">
              <button onClick={() => { setShowConfirmRevoke(false); setPendingAction(null); }} className="flex-1 px-4 py-3 border-2 border-gray-200 text-gray-700 rounded-full hover:bg-gray-50 font-medium transition-all whitespace-nowrap">
                Annuler
              </button>
              <button onClick={handleConfirmRevoke} className="flex-1 px-4 py-3 bg-yellow-600 text-white rounded-full hover:bg-yellow-700 font-semibold transition-all shadow-lg whitespace-nowrap">
                <i className="ri-check-line mr-2"></i>
                Oui, rétrograder
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-3xl shadow-xl border-2 border-clinic-blue/10 overflow-hidden">
          <div className="p-8">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="text-3xl font-bold text-clinic-blue-dark">Gestion des Administrateurs</h1>
                <p className="text-gray-600 mt-2">Créez, promouvez et gérez les administrateurs de la plateforme</p>
              </div>
              <button
                onClick={() => navigate('/admin')}
                className="px-4 py-2 text-clinic-blue hover:text-clinic-blue-dark transition-colors whitespace-nowrap"
              >
                ← Retour au tableau de bord
              </button>
            </div>

            {error && (
              <div className="mb-6 p-4 bg-red-50 border-2 border-red-200 text-red-600 rounded-2xl flex items-start">
                <i className="ri-error-warning-line text-xl mr-2 mt-0.5"></i>
                <span>{error}</span>
              </div>
            )}

            {success && (
              <div className="mb-6 p-4 bg-green-50 border-2 border-green-200 text-green-600 rounded-2xl flex items-start">
                <i className="ri-check-line text-xl mr-2 mt-0.5"></i>
                <span>{success}</span>
              </div>
            )}

              {/* Tabs */}
            <div className="mb-8">
              <div className="flex justify-between items-end">
                <div className="flex gap-2 border-b-2 border-gray-200">
                  <button
                    onClick={() => setActiveTab('list')}
                    className={`px-6 py-3 font-semibold transition-all whitespace-nowrap ${
                      activeTab === 'list'
                        ? 'text-clinic-blue border-b-4 border-clinic-blue -mb-0.5'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <i className="ri-shield-user-line mr-2"></i>
                    Administrateurs ({administrateurs.length})
                  </button>
                  <button
                    onClick={() => setActiveTab('create')}
                    className={`px-6 py-3 font-semibold transition-all whitespace-nowrap ${
                      activeTab === 'create'
                        ? 'text-clinic-blue border-b-4 border-clinic-blue -mb-0.5'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <i className="ri-user-add-line mr-2"></i>
                    Nouvel administrateur
                  </button>
                  <button
                    onClick={() => setActiveTab('promote')}
                    className={`px-6 py-3 font-semibold transition-all whitespace-nowrap ${
                      activeTab === 'promote'
                        ? 'text-clinic-blue border-b-4 border-clinic-blue -mb-0.5'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <i className="ri-arrow-up-circle-line mr-2"></i>
                    Promouvoir un utilisateur
                  </button>
                </div>
                <button
                  onClick={() => setShowImportModal(true)}
                  className="px-5 py-2.5 mb-0.5 bg-amber-500 text-white rounded-full hover:bg-amber-600 transition-all font-semibold text-sm whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-file-upload-line mr-2"></i>Importer (CSV)
                </button>
              </div>
            </div>

            {/* Tab: Liste des administrateurs */}
            {activeTab === 'list' && (
              <div className="space-y-4">
                {administrateurs.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    Aucun administrateur trouvé
                  </div>
                ) : (
                  administrateurs.map((admin: any) => (
                    <div key={admin.id} className="border-2 border-clinic-blue/10 rounded-2xl p-6 hover:shadow-lg transition-shadow">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center">
                          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mr-4">
                            <i className="ri-shield-user-line text-3xl text-red-600"></i>
                          </div>
                          <div>
                            <h3 className="text-xl font-bold text-clinic-blue-dark">
                              {admin.nom} {admin.prenom}
                            </h3>
                            <div className="flex items-center gap-2 mt-1 flex-wrap">
                              {admin.sexe && (
                                <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm font-medium">
                                  {admin.sexe}
                                </span>
                              )}
                              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                                admin.role === 'super_admin'
                                  ? 'bg-red-100 text-red-700'
                                  : 'bg-amber-100 text-amber-700'
                              }`}>
                                <i className={`${admin.role === 'super_admin' ? 'ri-vip-crown-line' : 'ri-shield-check-line'} mr-1`}></i>
                                {admin.role === 'super_admin' ? 'Super Admin' : 'Admin'}
                              </span>
                              {admin.isAlsoMedecin && (
                                <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                                  <i className="ri-stethoscope-line mr-1"></i>
                                  Médecin
                                </span>
                              )}
                              {admin.isAlsoSecretaire && (
                                <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium">
                                  <i className="ri-user-settings-line mr-1"></i>
                                  Secrétaire
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-500 mt-2">
                              <i className="ri-calendar-line mr-1"></i>
                              Ajouté le {new Date(admin.created_at).toLocaleDateString('fr-FR', {
                                day: 'numeric',
                                month: 'long',
                                year: 'numeric'
                              })}
                            </p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          {(admin.isAlsoMedecin || admin.isAlsoSecretaire) && (
                            <button
                              onClick={() => handleRevokeClick(admin.id, admin.user_id, `${admin.nom} ${admin.prenom}`)}
                              className="px-4 py-2 bg-yellow-100 text-yellow-700 rounded-full hover:bg-yellow-200 font-medium transition-all whitespace-nowrap"
                              title="Retirer le rôle admin uniquement (garde le compte actif)"
                            >
                              <i className="ri-arrow-down-circle-line mr-1"></i>
                              Rétrograder
                            </button>
                          )}
                          <button
                            onClick={() => handleDeleteClick(admin.id, admin.user_id, `${admin.nom} ${admin.prenom}`)}
                            className="px-4 py-2 bg-red-100 text-red-700 rounded-full hover:bg-red-200 font-medium transition-all whitespace-nowrap"
                          >
                            <i className="ri-delete-bin-line mr-1"></i>
                            {admin.isAlsoMedecin || admin.isAlsoSecretaire ? 'Rétrograder' : 'Supprimer'}
                          </button>
                          <button
                            onClick={() => {
                              setShowPasswordAdmin(showPasswordAdmin === admin.id ? null : admin.id);
                              setNewPasswordAdmin('');
                              setConfirmPasswordAdmin('');
                              setPasswordAdminError('');
                              setPasswordAdminSuccess('');
                            }}
                            className={`px-4 py-2 rounded-full font-medium transition-all whitespace-nowrap cursor-pointer ${
                              showPasswordAdmin === admin.id
                                ? 'bg-amber-200 text-amber-800'
                                : 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                            }`}
                            title="Changer le mot de passe"
                          >
                            <i className="ri-lock-password-line mr-1"></i>
                            Mot de passe
                          </button>
                        </div>
                      </div>
                      {showPasswordAdmin === admin.id && (
                        <div className="mt-4 p-4 bg-amber-50 border-2 border-amber-200 rounded-2xl">
                          <h4 className="font-semibold text-amber-800 mb-3 flex items-center gap-2 text-sm">
                            <i className="ri-lock-password-line"></i>
                            Changer le mot de passe de {admin.nom} {admin.prenom}
                          </h4>
                          {passwordAdminError && (
                            <div className="mb-3 p-3 bg-red-50 border border-red-200 text-red-600 rounded-xl text-sm flex items-start">
                              <i className="ri-error-warning-line mr-2 mt-0.5 flex-shrink-0"></i>
                              <span>{passwordAdminError}</span>
                            </div>
                          )}
                          {passwordAdminSuccess && (
                            <div className="mb-3 p-3 bg-green-50 border border-green-200 text-green-600 rounded-xl text-sm flex items-start">
                              <i className="ri-check-line mr-2 mt-0.5 flex-shrink-0"></i>
                              <span>{passwordAdminSuccess}</span>
                            </div>
                          )}
                          <div className="grid grid-cols-2 gap-3 mb-3">
                            <div>
                              <label className="block text-xs font-medium text-amber-700 mb-1">Nouveau mot de passe</label>
                              <input
                                type="password"
                                value={newPasswordAdmin}
                                onChange={(e) => setNewPasswordAdmin(e.target.value)}
                                className="w-full px-3 py-2 border-2 border-amber-200 rounded-xl focus:outline-none focus:border-amber-400 bg-white text-sm"
                                placeholder="Min. 10 caractères"
                                minLength={10}
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-amber-700 mb-1">Confirmer</label>
                              <input
                                type="password"
                                value={confirmPasswordAdmin}
                                onChange={(e) => setConfirmPasswordAdmin(e.target.value)}
                                className="w-full px-3 py-2 border-2 border-amber-200 rounded-xl focus:outline-none focus:border-amber-400 bg-white text-sm"
                                placeholder="Répéter"
                                minLength={10}
                              />
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => handlePasswordChangeAdmin(admin.user_id, `${admin.nom} ${admin.prenom}`)}
                              disabled={loadingPasswordAdmin || !newPasswordAdmin || !confirmPasswordAdmin}
                              className="px-4 py-2 bg-amber-600 text-white rounded-full hover:bg-amber-700 transition-all font-semibold text-sm disabled:opacity-50 whitespace-nowrap cursor-pointer"
                            >
                              {loadingPasswordAdmin ? (
                                <><i className="ri-loader-4-line animate-spin mr-1.5"></i>Changement...</>
                              ) : (
                                <><i className="ri-check-line mr-1.5"></i>Appliquer</>
                              )}
                            </button>
                            <button
                              onClick={() => {
                                setShowPasswordAdmin(null);
                                setNewPasswordAdmin('');
                                setConfirmPasswordAdmin('');
                                setPasswordAdminError('');
                              }}
                              className="px-4 py-2 bg-white border-2 border-amber-200 text-amber-700 rounded-full hover:bg-amber-100 transition-all font-semibold text-sm whitespace-nowrap cursor-pointer"
                            >
                              Annuler
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Tab: Nouvel administrateur (création de compte) */}
            {activeTab === 'create' && (
              <div>
                <div className="p-6 bg-blue-50 border-2 border-blue-200 rounded-2xl mb-6">
                  <h3 className="font-semibold text-blue-800 mb-2">
                    <i className="ri-information-line mr-2"></i>
                    Créer un compte administrateur depuis zéro
                  </h3>
                  <p className="text-sm text-blue-700">
                    Utilisez cette option si la personne n'a pas encore de compte sur la plateforme. Un nouveau compte sera créé avec un accès administrateur complet.
                  </p>
                </div>

                <div className="p-6 bg-clinic-blue/5 border-2 border-clinic-blue/20 rounded-2xl">
                  <h3 className="text-lg font-semibold text-clinic-blue-dark mb-4">Nouvel administrateur</h3>
                  <form onSubmit={handleCreateAdmin} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-clinic-blue-dark mb-2">Nom *</label>
                        <input
                          type="text"
                          required
                          value={createForm.nom}
                          onChange={(e) => setCreateForm({ ...createForm, nom: e.target.value })}
                          className="w-full px-4 py-3 border-2 border-clinic-blue/20 rounded-2xl focus:outline-none focus:border-clinic-blue text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-clinic-blue-dark mb-2">Prénom *</label>
                        <input
                          type="text"
                          required
                          value={createForm.prenom}
                          onChange={(e) => setCreateForm({ ...createForm, prenom: e.target.value })}
                          className="w-full px-4 py-3 border-2 border-clinic-blue/20 rounded-2xl focus:outline-none focus:border-clinic-blue text-sm"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-clinic-blue-dark mb-2">Sexe</label>
                      <select
                        value={createForm.sexe}
                        onChange={(e) => setCreateForm({ ...createForm, sexe: e.target.value })}
                        className="w-full px-4 py-3 border-2 border-clinic-blue/20 rounded-2xl focus:outline-none focus:border-clinic-blue text-sm pr-8"
                      >
                        <option value="">Sélectionner</option>
                        <option value="Masculin">Masculin</option>
                        <option value="Féminin">Féminin</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-clinic-blue-dark mb-2">Email *</label>
                      <input
                        type="email"
                        required
                        value={createForm.email}
                        onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                        className="w-full px-4 py-3 border-2 border-clinic-blue/20 rounded-2xl focus:outline-none focus:border-clinic-blue text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-clinic-blue-dark mb-2">Mot de passe *</label>
                      <input
                        type="password"
                        required
                        minLength={10}
                        value={createForm.motDePasse}
                        onChange={(e) => setCreateForm({ ...createForm, motDePasse: e.target.value })}
                        className="w-full px-4 py-3 border-2 border-clinic-blue/20 rounded-2xl focus:outline-none focus:border-clinic-blue text-sm"
                        placeholder="Minimum 10 caractères"
                      />
                    </div>
                    <div>
                      <button
                        type="submit"
                        disabled={loadingCreate}
                        className="px-6 py-3 bg-clinic-blue-dark text-white rounded-full hover:bg-clinic-blue transition-all font-semibold shadow-lg disabled:opacity-50 whitespace-nowrap"
                      >
                        {loadingCreate ? 'Création...' : 'Créer le compte administrateur'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {/* Tab: Promouvoir un utilisateur existant */}
            {activeTab === 'promote' && (
              <div>
                <div className="p-6 bg-green-50 border-2 border-green-200 rounded-2xl mb-6">
                  <h3 className="font-semibold text-green-800 mb-2">
                    <i className="ri-information-line mr-2"></i>
                    Promouvoir un utilisateur existant en administrateur
                  </h3>
                  <p className="text-sm text-green-700">
                    Cette option permet de donner les droits d'administration à un utilisateur qui a déjà un compte (médecin, secrétaire, etc.). Il conservera son rôle actuel ET obtiendra l'accès administrateur.
                  </p>
                </div>

                <div className="p-6 bg-clinic-blue/5 border-2 border-clinic-blue/20 rounded-2xl">
                  <h3 className="text-lg font-semibold text-clinic-blue-dark mb-4">Promouvoir un utilisateur</h3>
                  <form onSubmit={handlePromoteClick} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-clinic-blue-dark mb-2">
                        Email de l'utilisateur à promouvoir *
                      </label>
                      <input
                        type="email"
                        required
                        value={promoteEmail}
                        onChange={(e) => setPromoteEmail(e.target.value)}
                        className="w-full px-4 py-3 border-2 border-clinic-blue/20 rounded-2xl focus:outline-none focus:border-clinic-blue text-sm"
                        placeholder="ex: dr.dupond@clinique.fr"
                      />
                      <p className="text-xs text-gray-500 mt-2">
                        L'utilisateur doit déjà avoir un compte actif sur la plateforme (médecin, secrétaire, etc.).
                      </p>
                    </div>
                    <div>
                      <button
                        type="submit"
                        disabled={loadingPromote || !promoteEmail}
                        className="px-6 py-3 bg-green-600 text-white rounded-full hover:bg-green-700 transition-all font-semibold shadow-lg disabled:opacity-50 whitespace-nowrap"
                      >
                        {loadingPromote ? (
                          <>
                            <i className="ri-loader-4-line animate-spin mr-2"></i>
                            Promotion en cours...
                          </>
                        ) : (
                          <>
                            <i className="ri-arrow-up-circle-line mr-2"></i>
                            Promouvoir en administrateur
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                </div>

                <div className="mt-6 p-6 bg-amber-50 border-2 border-amber-200 rounded-2xl">
                  <h3 className="font-semibold text-amber-800 mb-2">
                    <i className="ri-lightbulb-line mr-2"></i>
                    Sécurité renforcée
                  </h3>
                  <p className="text-sm text-amber-700">
                    La promotion d'un utilisateur nécessite une <strong>double confirmation</strong> : vous devrez d'abord confirmer votre intention, puis saisir votre <strong>mot de passe administrateur</strong> pour valider. Toutes les promotions sont <strong>enregistrées dans les journaux d'audit</strong> avec la date, l'heure, l'adresse IP et l'identité de l'administrateur.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <BulkImportStaffModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        staffType="super_admin"
        onSuccess={loadAdministrateurs}
      />

      <Footer />
    </div>
  );
}